// Slug and route metadata utilities for Australia↔India SEO landing pages.

export function toSlug(city) {
  return String(city || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export function fromSlug(slug) {
  return String(slug || '')
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

// Display names for the cities used on route pages (must match airports.js).
const CITY_CODES = {
  Sydney: { code: 'SYD', label: 'Sydney' },
  Melbourne: { code: 'MEL', label: 'Melbourne' },
  Brisbane: { code: 'BNE', label: 'Brisbane' },
  Perth: { code: 'PER', label: 'Perth' },
  Adelaide: { code: 'ADL', label: 'Adelaide' },
  'New Delhi': { code: 'DEL', label: 'New Delhi' },
  Mumbai: { code: 'BOM', label: 'Mumbai' },
  Bengaluru: { code: 'BLR', label: 'Bengaluru' },
  Hyderabad: { code: 'HYD', label: 'Hyderabad' },
  Chennai: { code: 'MAA', label: 'Chennai' },
  Ahmedabad: { code: 'AMD', label: 'Ahmedabad' },
  Dubai: { code: 'DXB', label: 'Dubai' },
  Singapore: { code: 'SIN', label: 'Singapore' },
  Bangkok: { code: 'BKK', label: 'Bangkok' },
  Denpasar: { code: 'DPS', label: 'Denpasar' },
  London: { code: 'LHR', label: 'London' },
};

export function getCityCode(city) {
  return CITY_CODES[city]?.code || '';
}

// Approximate non-stop flight-time label per origin, used in landing-page copy.
const FLIGHT_TIME = {
  Sydney: 'around 12 to 13 hours',
  Melbourne: 'around 11 to 12 hours',
  Brisbane: 'around 12 to 13 hours',
  Perth: 'around 10 to 11 hours',
  Adelaide: 'around 12 to 13 hours',
};

export function flightTimeLabel(origin) {
  return FLIGHT_TIME[origin] || 'around 11 to 13 hours';
}

// General notes used across route pages (kept factual and non-fabricated).
const ROUTE_AIRLINES = {
  India: ['Qantas', 'Air India', 'Singapore Airlines', 'Emirates', 'Malaysia Airlines', 'Thai Airways International'],
  Dubai: ['Emirates', 'Qantas'],
  Singapore: ['Singapore Airlines', 'Qantas', 'Jetstar'],
  Bangkok: ['Thai Airways International', 'Qantas', 'Jetstar'],
  Denpasar: ['Qantas', 'Jetstar', 'Virgin Australia'],
  London: ['Qantas', 'British Airways', 'Emirates', 'Singapore Airlines', 'Virgin Atlantic'],
};

const DEST_COUNTRY = {
  'New Delhi': 'India',
  Mumbai: 'India',
  Bengaluru: 'India',
  Hyderabad: 'India',
  Chennai: 'India',
  Ahmedabad: 'India',
  Dubai: 'UAE',
  Singapore: 'Singapore',
  Bangkok: 'Thailand',
  Denpasar: 'Indonesia',
  London: 'United Kingdom',
};

export function destinationCountry(city) {
  return DEST_COUNTRY[city] || '';
}

export function routeAirlines(destination) {
  const country = destinationCountry(destination);
  if (country) return ROUTE_AIRLINES[country] || [];
  return [];
}

// Rough indicative distance (km) for common AU↔destination combos. Falls back gracefully.
const ROUTE_DISTANCE = {
  Sydney: { 'New Delhi': 10380, Mumbai: 9650, Bengaluru: 9020, Dubai: 12040, Singapore: 6300, Bangkok: 7500, Denpasar: 4600, London: 17000 },
  Melbourne: { 'New Delhi': 10100, Mumbai: 9600, Bengaluru: 9550, Dubai: 11600, Singapore: 6000, Bangkok: 7300, Denpasar: 5000, London: 16900 },
  Perth: { 'New Delhi': 7800, Mumbai: 7300, Singapore: 3900 },
  Brisbane: { 'New Delhi': 11300 },
  Adelaide: { 'New Delhi': 10400 },
};

export function routeDistance(origin, destination) {
  return ROUTE_DISTANCE[origin]?.[destination] || null;
}

export function formatDistanceKm(km) {
  if (!km) return null;
  return km >= 1000 ? `${(km / 1000).toFixed(1)}k km` : `${km} km`;
}

export function getTodayDateString() {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// Returns tomorrow's date as YYYY-MM-DD — a valid default search date.
// Mirrors SearchFlights' getDefaultSearchDate() so deep-linked auto-searches
// always send a real departure date (empty dates cause backend search failures).
export function getDefaultSearchDate() {
  const now = new Date();
  const d = new Date(now.getTime() + 24 * 60 * 60 * 1000);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}
